// src/hooks/useDebounce.js
import { useState, useEffect, useCallback } from 'react';
import { debounce } from '../utils/requestUtils';

/**
 * Custom hook for debouncing a value (e.g. search input)
 * @param {any} value - The value to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {any} The debounced value
 */
const useDebounce = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    // Set up the debounce timer
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Clean up the timer when value changes again before delay is complete
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Custom hook for debounced search with loading state
 * @param {string} searchTerm - The search term
 * @param {number} delay - Debounce delay in milliseconds  
 * @returns {Object} Object with debouncedTerm and isSearching
 */
export const useDebouncedSearch = (searchTerm, delay = 300) => {
  const [debouncedTerm, setDebouncedTerm] = useState(searchTerm);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (searchTerm !== debouncedTerm) {
      setIsSearching(true);
    }

    const handler = setTimeout(() => {
      setDebouncedTerm(searchTerm);
      setIsSearching(false);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, debouncedTerm, delay]);

  return {
    debouncedTerm,
    isSearching
  };
};

/**
 * Custom hook for debounced callback function
 * @param {Function} callback - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced callback function
 */
export const useDebouncedCallback = (callback, delay = 300) => {
  return useCallback(
    debounce(callback, delay),
    [callback, delay]
  );
};

export default useDebounce;